import React, { useMemo } from 'react';
import {
  Play,
  Envelope,
  DownloadSimple,
  ShareFat,
  CurrencyDollarSimple,
  Icon,
  IconWeight,
} from '@phosphor-icons/react';
import classNames from 'classnames';

import { useWindowWidth } from '../../../../hooks/useWindowWidth';

import { useProjectStore } from '../../../../stores/project';

import { Person, Payment } from '../../../../types';
import { round, getPct, getBpWidth } from '../../../../utils';

interface Props {
  people: Person[];
  showPages?: string[];
}

type MetricStatsBase = { pct: number; total: number };
type MetricStatsAvg = MetricStatsBase & { avg: number };
type MetricStatsSum = MetricStatsBase & { sum: number };

type Stats = {
  emailed: MetricStatsAvg;
  viewed: MetricStatsAvg;
  downloaded: MetricStatsAvg;
  shared: MetricStatsAvg;
  paid: MetricStatsSum;
};

const StatsBlock: React.FC<Props> = (props) => {
  const { people, showPages } = props;

  const { currentProject } = useProjectStore();

  const windowWidth = useWindowWidth();
  const isMobileDevice = useMemo(
    () => windowWidth < getBpWidth('lg'),
    [windowWidth],
  );

  const paymentObj: Payment | null = currentProject?.payment ?? null;

  const stats = useMemo<Stats>(() => {
    const totalPeople = people.length;

    let emailedNum = 0;
    let emailedTotal = 0;

    let viewedNum = 0;
    let viewedTotal = 0;

    let downloadedNum = 0;
    let downloadedTotal = 0;

    let sharedNum = 0;
    let sharedTotal = 0;

    let paidTotal = 0;
    let paidSum = 0;

    people.forEach((person) => {
      const { metrics } = person;

      if (!metrics) return;

      if ((metrics.emailedCount ?? 0) >= 1) emailedNum++;
      emailedTotal += metrics.emailedCount ?? 0;

      if ((metrics.viewedCount ?? 0) >= 1) viewedNum++;
      viewedTotal += metrics.viewedCount ?? 0;

      if ((metrics.downloadedCount ?? 0) >= 1) downloadedNum++;
      downloadedTotal += metrics.downloadedCount ?? 0;

      if ((metrics.sharedCount ?? 0) >= 1) sharedNum++;
      sharedTotal += metrics.sharedCount ?? 0;

      if (person.face?.payment?.paid) {
        paidTotal++;

        if (person.face.payment.type === 'donate')
          paidSum += person.face.payment.amount ?? 0;
      }
    });

    const emailedPct = getPct(emailedNum / totalPeople) || 0;
    const emailedAvg = round(emailedTotal / totalPeople) || 0;

    const viewedPct = getPct(viewedNum / totalPeople) || 0;
    const viewedAvg = round(viewedTotal / totalPeople) || 0;

    const downloadedPct = getPct(downloadedNum / totalPeople) || 0;
    const downloadedAvg = round(downloadedTotal / totalPeople) || 0;

    const sharedPct = getPct(sharedNum / totalPeople) || 0;
    const sharedAvg = round(sharedTotal / totalPeople) || 0;

    const paidPct = getPct(paidTotal / totalPeople) || 0;

    return {
      emailed: { pct: emailedPct, total: emailedTotal, avg: emailedAvg },
      viewed: { pct: viewedPct, total: viewedTotal, avg: viewedAvg },
      downloaded: {
        pct: downloadedPct,
        total: downloadedTotal,
        avg: downloadedAvg,
      },
      shared: { pct: sharedPct, total: sharedTotal, avg: sharedAvg },
      paid: { pct: paidPct, total: paidTotal, sum: paidSum },
    };
  }, [people]);

  const { emailed, viewed, downloaded, shared, paid } = stats;

  const avg = isMobileDevice ? 'Avg.' : 'Average';

  const data: {
    mainData: {
      Icon: Icon;
      iconWeight?: IconWeight;
      text: string;
      num: number;
    };
    otherData: (
      | { text: string; num: number; isCurrency?: boolean; isEmptyLine?: false }
      | { isEmptyLine: true }
    )[];
    show?: boolean;
  }[] = [
    {
      mainData: { Icon: Envelope, text: 'Attendees emailed', num: emailed.pct },
      otherData: [
        { text: 'Total emails sent', num: emailed.total },
        { text: `${avg} emails per attendee`, num: emailed.avg },
      ],
      show: !showPages || showPages.includes('emailed'),
    },
    {
      mainData: { Icon: Play, text: 'Videos played', num: viewed.pct },
      otherData: [
        { text: 'Total video plays', num: viewed.total },
        { text: `${avg} plays per video`, num: viewed.avg },
      ],
      show: !showPages || showPages.includes('played'),
    },
    {
      mainData: {
        Icon: DownloadSimple,
        text: 'Videos downloaded',
        num: downloaded.pct,
      },
      otherData: [
        { text: 'Total video downloads', num: downloaded.total },
        { text: `${avg} downloads per video`, num: downloaded.avg },
      ],
      show: !showPages || showPages.includes('downloaded'),
    },
    {
      mainData: { Icon: ShareFat, text: 'Videos shared', num: shared.pct },
      otherData: [
        { text: 'Total video shares', num: shared.total },
        { text: `${avg} shares per video`, num: shared.avg },
      ],
      show: !showPages || showPages.includes('shared'),
    },
    {
      mainData: {
        Icon: CurrencyDollarSimple,
        iconWeight: 'bold',
        text: 'Attendees paid',
        num: paid.pct,
      },
      otherData: [
        { text: 'Total payments made', num: paid.total },
        { isEmptyLine: true },
      ],
      show: !paymentObj?.paid && (!showPages || showPages.includes('payment')),
    },
    {
      mainData: {
        Icon: CurrencyDollarSimple,
        iconWeight: 'bold',
        text: 'Attendees donated',
        num: paid.pct,
      },
      otherData: [
        { text: 'Total donations made', num: paid.total },
        { text: 'Sum of donations', num: paid.sum, isCurrency: true },
      ],
      show:
        paymentObj?.paid &&
        (paymentObj?.allowDonations ?? true) &&
        (!showPages || showPages.includes('payment')),
    },
  ];

  const numOfBlocks = data.filter((e) => e.show).length;

  return (
    <div className="flex flex-wrap sm:flex-nowrap gap-4">
      {data.map(
        (metric) =>
          metric.show && (
            <div
              key={metric.mainData.text}
              className={classNames({
                'w-[calc((100%_-_1rem)_/_2)]': true,
                'sm:w-[calc((var(--page-width)_-_(1rem_*_4))_/_5)]':
                  !!showPages || numOfBlocks === 5,
                'sm:w-[calc((var(--page-width)_-_(1rem_*_3))_/_4)]':
                  !showPages && numOfBlocks === 4,
              })}
            >
              <div className="flex p-3 sm:p-4 gap-3 sm:gap-4 items-center rounded-t-md border border-black border-solid">
                <div className="flex items-center justify-center p-2 sm:p-3 text-lg sm:text-[1.25rem] text-white bg-black rounded-full">
                  <metric.mainData.Icon
                    weight={metric.mainData.iconWeight || 'fill'}
                  />
                </div>

                <div className="grow basis-0 min-w-0">
                  <div className="text-md sm:text-lg font-bold">
                    {metric.mainData.num ?? 0}%
                  </div>

                  <div className="text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                    {metric.mainData.text}
                  </div>
                </div>
              </div>

              <div className="flex flex-col p-3 sm:p-4 gap-1 text-white bg-black rounded-b-md">
                {metric.otherData.map((el, index) =>
                  el.isEmptyLine ? (
                    <div
                      key={`empty-${index}`}
                      className="h-[calc(0.825rem_*_1.5)]"
                    />
                  ) : (
                    <div
                      key={el.text}
                      className="flex items-center justify-between gap-1"
                    >
                      <div className="text-xs sm:text-sm whitespace-nowrap overflow-hidden text-ellipsis">
                        {el.text}
                      </div>

                      <div className="text-sm font-bold">
                        {el.isCurrency ? `$${el.num}` : el.num}
                      </div>
                    </div>
                  ),
                )}
              </div>
            </div>
          ),
      )}
    </div>
  );
};

export default StatsBlock;
